import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MdCircle, MdCheckCircle, MdKeyboardArrowDown } from 'react-icons/md';
import { BiLoaderCircle } from 'react-icons/bi';
import { FaUserCircle } from 'react-icons/fa';
import { MdQrCode2 } from 'react-icons/md';
import { QRCodeSVG } from 'qrcode.react';
import TaskCard from './TaskCard';
import ResolveTaskModal from './ResolveTaskModal';
import '../../TaskManagement.css';

const RESOLVED_PAGE_SIZE = 12;

// Idle timeout: redirect to home if no activity within this duration
const IDLE_TIMEOUT_MS = 1 * 60 * 1000; // 1 minute — change multiplier to adjust

// Map UI status to AlarmSearchStatus enum values (comma-separated)
// AlarmSearchStatus: ANY, ACTIVE, CLEARED, ACK, UNACK
const STATUS_TO_ALARM_STATUS = {
  'Open': 'ACTIVE,UNACK',        // Active and unacknowledged
  'In Progress': 'ACTIVE,ACK',   // Active and acknowledged
  'Resolved': 'CLEARED,ACK',     // Cleared and acknowledged
};

function StaffFilterDropdown({ staffFilter, setStaffFilter, staffUsers }) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  const options = useMemo(() => [
    { value: 'all', label: 'All Staff' },
    ...staffUsers.map((s) => ({ value: s.tbUserId, label: s.name })),
  ], [staffUsers]);

  const selectedLabel = options.find((o) => o.value === staffFilter)?.label || 'All Staff';

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  return (
    <div className="filter-group staff-filter-group">
      <span className="filter-label">Staff Member Filter</span>
      <div className="custom-dropdown" ref={dropdownRef}>
        <button
          type="button"
          className={`custom-dropdown-trigger ${open ? 'open' : ''}`}
          onClick={() => setOpen((prev) => !prev)}
        >
          <span>{selectedLabel}</span>
          <MdKeyboardArrowDown className={`custom-dropdown-arrow ${open ? 'rotated' : ''}`} />
        </button>
        {open && (
          <ul className="custom-dropdown-menu">
            {options.map((opt) => (
              <li
                key={opt.value}
                className={`custom-dropdown-item ${staffFilter === opt.value ? 'selected' : ''}`}
                onClick={() => { setStaffFilter(opt.value); setOpen(false); }}
              >
                {opt.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function TaskList({ assetId, onBackHome, staffInfo, tenantId, customerId }) {
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [statusFilters, setStatusFilters] = useState(['In Progress']);
  const [staffFilter, setStaffFilter] = useState('all'); // For supervisor filter
  const [resolvedTimeFilter, setResolvedTimeFilter] = useState(null); // null = no filter (show all)
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [staffUsers, setStaffUsers] = useState([]); // Staff users for supervisor dropdown
  const [isLoading, setIsLoading] = useState(false);
  const lastFetchParams = useRef(''); // Track last fetch params to prevent duplicate calls
  const idleTimerRef = useRef(null);

  // QR code modal state (supervisor only)
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrDeepLink, setQrDeepLink] = useState('');
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState('');

  const isSupervisor = staffInfo?.role === 'staff_admin';

  // Keep a ref to onBackHome so the idle timer always calls the latest version
  // without causing resetIdleTimer to change reference on every parent re-render
  const onBackHomeRef = useRef(onBackHome);
  useEffect(() => {
    onBackHomeRef.current = onBackHome;
  }, [onBackHome]);

  // Stable reset function — no deps that change on parent re-renders
  const resetIdleTimer = useCallback(() => {
    clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      onBackHomeRef.current();
    }, IDLE_TIMEOUT_MS);
  }, []);

  // Start idle timer on mount; use document-level listeners so interactions
  // inside modals (which use stopPropagation) also reset the timer
  useEffect(() => {
    resetIdleTimer();
    document.addEventListener('click', resetIdleTimer, true);
    document.addEventListener('touchstart', resetIdleTimer, true);
    return () => {
      clearTimeout(idleTimerRef.current);
      document.removeEventListener('click', resetIdleTimer, true);
      document.removeEventListener('touchstart', resetIdleTimer, true);
    };
  }, [resetIdleTimer]);

  // Fetch mobile app QR deep link
  const fetchQrDeepLink = useCallback(async (signal) => {
    if (!staffInfo?.bearerToken) {
      setQrError('No authentication token available');
      setQrLoading(false);
      return;
    }

    setQrLoading(true);
    setQrError('');

    try {
      const apiBase = process.env.REACT_APP_API_BASE !== undefined ? process.env.REACT_APP_API_BASE : 'http://localhost:3001';

      const response = await fetch(`${apiBase}/api/mobile-qr`, {
        headers: {
          'Authorization': `Bearer ${staffInfo.bearerToken}`,
        },
        signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.ok && data.deepLink) {
        setQrDeepLink(data.deepLink);
      } else {
        setQrError(data.message || data.error || 'Failed to generate QR code');
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      setQrError('Failed to connect to server');
    } finally {
      setQrLoading(false);
    }
  }, [staffInfo?.bearerToken]);

  // Store AbortController ref for QR fetch
  const qrAbortRef = useRef(null);

  // Handle QR button click
  const handleShowQr = () => {
    setQrLoading(true); // Show loading immediately
    setQrError('');
    setShowQrModal(true);
    if (!qrDeepLink) {
      qrAbortRef.current = new AbortController();
      fetchQrDeepLink(qrAbortRef.current.signal);
    } else {
      setQrLoading(false);
    }
  };

  // Cleanup QR fetch on modal close
  const handleCloseQrModal = () => {
    qrAbortRef.current?.abort();
    setShowQrModal(false);
  };

  // Get alarm status string for current filter (single selection)
  const getAlarmStatus = useCallback(() => {
    const selectedStatus = statusFilters[0]; // Single selection
    return STATUS_TO_ALARM_STATUS[selectedStatus] || '';
  }, [statusFilters]);

  // Compute startTime/endTime (epoch ms) for resolved task time filters
  const getResolvedTimeRange = useCallback(() => {
    const now = Date.now();
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    switch (resolvedTimeFilter) {
      case '24h':
        return { startTime: now - 24 * 60 * 60 * 1000, endTime: now };
      case 'today':
        return { startTime: startOfToday.getTime(), endTime: now };
      case 'yesterday': {
        const startOfYesterday = new Date(startOfToday);
        startOfYesterday.setDate(startOfYesterday.getDate() - 1);
        return { startTime: startOfYesterday.getTime(), endTime: startOfToday.getTime() - 1 };
      }
      case '7d':
        return { startTime: now - 7 * 24 * 60 * 60 * 1000, endTime: now };
      default:
        return { startTime: now - 24 * 60 * 60 * 1000, endTime: now };
    }
  }, [resolvedTimeFilter]);

  // Fetch tasks from API
  const fetchTasks = useCallback(async (signal) => {
    if (!staffInfo) return;

    try {
      const tasksApiUrl = process.env.REACT_APP_TASKS_API;
      if (!tasksApiUrl) {
        return;
      }

      // Build URL with query parameters
      const url = new URL(tasksApiUrl);

      // The asset this kiosk is linked to — a Floor or a single Washroom. The
      // query param is named `floorId` for compatibility with the Get Incidents
      // webhook, but it is not floor-only: the webhook queries alarms at
      // whatever asset id it is given, and alarms propagate up to their
      // washroom as well as their floor. A washroom-linked kiosk therefore
      // lists that washroom's tasks, a floor-linked one the whole floor.
      if (assetId) {
        url.searchParams.append('floorId', assetId);
      }

      // Pass alarm status based on selected filter (e.g., "ACTIVE,UNACK")
      const alarmStatus = getAlarmStatus();
      if (alarmStatus) {
        url.searchParams.append('status', alarmStatus);
      }

      // For resolved tasks, pass time range and pagination hints to the API
      if (statusFilters[0] === 'Resolved') {
        if (resolvedTimeFilter) {
          const { startTime, endTime } = getResolvedTimeRange();
          url.searchParams.append('startTime', startTime);
          url.searchParams.append('endTime', endTime);
        }
      }

      // Pass assigneeId (ThingsBoard User ID) to fetch tasks for specific staff
      // - For regular staff: always pass their own tbUserId
      // - For supervisors: pass selected staff's tbUserId when filtering by specific staff
      if (!isSupervisor && staffInfo?.tbUserId) {
        url.searchParams.append('assigneeId', staffInfo.tbUserId);
      } else if (isSupervisor && staffFilter !== 'all') {
        url.searchParams.append('assigneeId', staffFilter);
      }

      // Prevent duplicate calls with same parameters
      const fetchParams = url.toString();
      if (fetchParams === lastFetchParams.current) {
        return;
      }
      lastFetchParams.current = fetchParams;

      const headers = {};
      if (staffInfo?.bearerToken) {
        headers['Authorization'] = `Bearer ${staffInfo.bearerToken}`;
      }

      setIsLoading(true);
      const response = await fetch(url.toString(), { headers, signal });
      if (!response.ok) {
        throw new Error(`Failed to fetch tasks: ${response.statusText}`);
      }

      const data = await response.json();
      setTasks(data);
    } catch (error) {
      if (error.name === 'AbortError') return;
      setTasks([]);
    } finally {
      setIsLoading(false);
    }
  }, [staffInfo, assetId, isSupervisor, getAlarmStatus, staffFilter, statusFilters, resolvedTimeFilter, getResolvedTimeRange]);

  // Fetch tasks when dependencies change
  useEffect(() => {
    const controller = new AbortController();
    fetchTasks(controller.signal);
    return () => controller.abort();
  }, [fetchTasks]);

  // Fetch staff users for supervisor dropdown (only staff_user role)
  useEffect(() => {
    if (!isSupervisor || !tenantId || !customerId) return;
    if (!staffInfo?.bearerToken) return; // bpmpp-api now requires a TB Bearer

    const controller = new AbortController();

    const fetchStaffUsers = async () => {
      try {
        const apiBase = process.env.REACT_APP_API_BASE !== undefined ? process.env.REACT_APP_API_BASE : 'http://localhost:3001';
        const response = await fetch(
          `${apiBase}/api/staff-users?tenantId=${encodeURIComponent(tenantId)}&customerId=${encodeURIComponent(customerId)}`,
          {
            signal: controller.signal,
            headers: { 'Authorization': `Bearer ${staffInfo.bearerToken}` },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (data.ok && data.users) {
          // Filter to only show staff_user role (exclude supervisors/admins)
          // Case insensitive check for role
          const regularStaff = data.users.filter((user) => {
            const role = user.role?.toLowerCase();
            return role === 'user' || role === 'staff';
          });
          setStaffUsers(regularStaff);
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[TaskList] Failed to fetch staff users:', error);
      }
    };

    fetchStaffUsers();
    return () => controller.abort();
  }, [isSupervisor, tenantId, customerId, staffInfo?.bearerToken]);

  // Apply client-side time filter for resolved tasks
  useEffect(() => {
    if (statusFilters[0] !== 'Resolved' || !resolvedTimeFilter) {
      setFilteredTasks([...tasks]);
      return;
    }

    const { startTime, endTime } = getResolvedTimeRange();
    const filtered = tasks.filter((task) => {
      const ts = new Date(task.resolvedAt || task.updatedAt || task.createdAt).getTime();
      return ts >= startTime && ts <= endTime;
    });
    setFilteredTasks(filtered);
  }, [tasks, statusFilters, resolvedTimeFilter, getResolvedTimeRange]);

  // Reset to page 1 when filters or data change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilters, resolvedTimeFilter]);

  // Single selection - only one status filter at a time
  const selectStatusFilter = (status) => {
    setStatusFilters([status]);
    if (status !== 'Resolved') setResolvedTimeFilter(null);
  };

  const handleResolveClick = (task) => {
    setSelectedTask(task);
    setShowResolveModal(true);
  };

  const handleTaskResolved = (taskId) => {
    setTasks((prev) => prev.filter((task) => task.id !== taskId));
    setShowResolveModal(false);
    setSelectedTask(null);
  };

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = String(date.getFullYear()).slice(-2);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  // Pagination — only applied for the Resolved tab
  const isResolvedTab = statusFilters[0] === 'Resolved';
  const totalPages = isResolvedTab ? Math.ceil(filteredTasks.length / RESOLVED_PAGE_SIZE) : 1;
  const paginatedTasks = isResolvedTab
    ? filteredTasks.slice((currentPage - 1) * RESOLVED_PAGE_SIZE, currentPage * RESOLVED_PAGE_SIZE)
    : filteredTasks;

  // Build visible page numbers (max 5, centred around currentPage)
  const getPageNumbers = () => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
    return Array.from({ length: 5 }, (_, i) => start + i);
  };

  return (
    <div className="task-list-container">
      {/* Header */}
      <div className="task-list-header">
        <button className="task-home-btn" onClick={onBackHome} aria-label="Back to home">
          🏠
        </button>
        <div className="staff-profile">
          {staffInfo?.photo ? (
            <img src={staffInfo.photo} alt="Staff avatar" className="staff-avatar" />
          ) : (
            <FaUserCircle
              className="staff-avatar-icon"
              style={{ color: staffInfo?.gender === 'female' ? '#ec4899' : '#3b82f6' }}
            />
          )}
          <div className="staff-info">
            <h3 className="staff-name">{staffInfo?.name || 'Staff Member'}</h3>
            <p className="staff-title">{staffInfo?.title || 'Housekeeping Staff'}</p>
          </div>
        </div>
        <h2 className="task-list-title">My Tasks</h2>

        {/* QR Code button - Supervisor only */}
        {isSupervisor && (
          <button className="qr-code-btn" onClick={handleShowQr} aria-label="Show QR Code">
            <MdQrCode2 size={28} />
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="task-filters">
        <div className="filter-row">
          <div className="filter-group">
            <span className="filter-label">Task Status Filter</span>
            <div className="filter-chips">
              <button
                className={`filter-chip ${statusFilters.includes('Open') ? 'active' : ''}`}
                onClick={() => selectStatusFilter('Open')}
              >
                <MdCircle className="filter-icon" style={{ color: statusFilters.includes('Open') ? 'white' : '#f59e0b' }} />
                Open{statusFilters.includes('Open') ? ` (${tasks.length})` : ''}
              </button>
              <button
                className={`filter-chip ${statusFilters.includes('In Progress') ? 'active' : ''}`}
                onClick={() => selectStatusFilter('In Progress')}
              >
                <BiLoaderCircle className="filter-icon" style={{ color: statusFilters.includes('In Progress') ? 'white' : '#3b82f6' }} />
                In Progress{statusFilters.includes('In Progress') ? ` (${tasks.length})` : ''}
              </button>
              <button
                className={`filter-chip ${statusFilters.includes('Resolved') ? 'active' : ''}`}
                onClick={() => selectStatusFilter('Resolved')}
              >
                <MdCheckCircle className="filter-icon" style={{ color: statusFilters.includes('Resolved') ? 'white' : '#10b981' }} />
                Completed{statusFilters.includes('Resolved') ? ` (${tasks.length})` : ''}
              </button>
            </div>
          </div>

          {/* Staff Filter - Only for Supervisors */}
          {isSupervisor && (
            <StaffFilterDropdown
              staffFilter={staffFilter}
              setStaffFilter={setStaffFilter}
              staffUsers={staffUsers}
            />
          )}
        </div>

        {/* Time Range Filter - Second row, only for Resolved tasks */}
        {statusFilters[0] === 'Resolved' && (
          <div className="filter-row" style={{ marginTop: 16 }}>
            <div className="filter-group">
              <span className="filter-label">Time Range</span>
              <div className="filter-chips">
                {[
                  { value: '24h', label: 'Last 24 Hours' },
                  { value: 'today', label: 'Today' },
                  { value: 'yesterday', label: 'Yesterday' },
                  { value: '7d', label: 'Last 7 Days' },
                ].map(({ value, label }) => (
                  <button
                    key={value}
                    className={`filter-chip ${resolvedTimeFilter === value ? 'active' : ''}`}
                    onClick={() => setResolvedTimeFilter(resolvedTimeFilter === value ? null : value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Task List */}
      <div className="task-cards-container">
        {isLoading ? (
          <div className="no-tasks-message">
            <BiLoaderCircle className="loading-spinner" />
            <p>Loading tasks...</p>
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="no-tasks-message">
            <p>No tasks found matching the selected filters.</p>
          </div>
        ) : (
          paginatedTasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              formatDateTime={formatDateTime}
              onResolveClick={handleResolveClick}
              isSupervisor={isSupervisor}
            />
          ))
        )}
      </div>

      {/* Pagination — Resolved tab only, when more than one page */}
      {isResolvedTab && !isLoading && totalPages > 1 && (
        <div className="task-pagination">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage((p) => p - 1)}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            &#8249;
          </button>

          {getPageNumbers().map((page) => (
            <button
              key={page}
              className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
              onClick={() => setCurrentPage(page)}
              aria-label={`Page ${page}`}
            >
              {page}
            </button>
          ))}

          <button
            className="pagination-btn"
            onClick={() => setCurrentPage((p) => p + 1)}
            disabled={currentPage === totalPages}
            aria-label="Next page"
          >
            &#8250;
          </button>

          <span className="pagination-info">
            {(currentPage - 1) * RESOLVED_PAGE_SIZE + 1}–{Math.min(currentPage * RESOLVED_PAGE_SIZE, filteredTasks.length)} of {filteredTasks.length}
          </span>
        </div>
      )}

      {/* Resolve Task Modal */}
      {showResolveModal && selectedTask && (
        <ResolveTaskModal
          task={selectedTask}
          onClose={() => setShowResolveModal(false)}
          onTaskResolved={handleTaskResolved}
          bearerToken={staffInfo?.bearerToken}
          refreshToken={staffInfo?.refreshToken}
        />
      )}

      {/* QR Code Modal - Supervisor only */}
      {showQrModal && (
        <div className="modal-overlay" onClick={handleCloseQrModal}>
          <div className="qr-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="qr-modal-header">
              <h3>MyHuB. Mobile App</h3>
              <button className="qr-modal-close" onClick={handleCloseQrModal}>
                ✕
              </button>
            </div>
            <div className="qr-modal-body">
              {qrLoading ? (
                <div className="qr-loading">
                  <BiLoaderCircle className="loading-spinner" />
                  <p>Generating QR Code...</p>
                </div>
              ) : qrError ? (
                <div className="qr-error">
                  <p>{qrError}</p>
                  <button className="qr-retry-btn" onClick={fetchQrDeepLink}>
                    Retry
                  </button>
                </div>
              ) : qrDeepLink ? (
                <div className="qr-display">
                  <QRCodeSVG
                    value={qrDeepLink}
                    size={200}
                    level="M"
                    includeMargin={true}
                  />
                  <p className="qr-instructions">
                    Scan with MyHuB. Mobile App to login automatically
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TaskList;
